import { z } from 'zod';
import type { SimNao } from '@/types';
import type { DeepUndefinedOrNull } from '@/types/partial';
import { EmpresaUsuarioSchema } from '@/features/painel-administrativo/empresa/page/edit/usuario/cadastro-usuario/types';
import { AmbienteFiscal, IndicadorIe, TipoPessoa } from '@/types/enum';
import { EmpresaSlugSchema } from '../../empresa-slug/types/EmpresaSlug';
import { RepresentanteSchema } from './EmpresaRepresentante';
import {
    EmpresaCrtCrt,
    EmpresaImpostoFunruralDescontar,
    EmpresaMicro,
    EmpresaPermiteCredito,
    EmpresaSistemaTipoUso,
    EmpresaSituacao,
    EmpresaTipoEmissao,
} from './enum';

const EmpresaCrtSchema = z.object({
    crt_crt: z.enum(EmpresaCrtCrt),
    crt_descricao: z.string(),
    crt_idpk: z.number(),
});

const EmpresaCidadeSchema = z.object({
    cid_cidade: z.string(),
    cid_ibge: z.string(),
    cid_idpk: z.number().optional(),
    cid_pais_idpk: z.number(),
    cid_timezone: z.string(),
    cid_uf: z.string(),
    cid_ultima_alteracao: z.string().nullish(),
    pais: z.string(),
});

const EmpresaSuporteSchema = z.object({
    esu_empresa_idpk: z.number(),
    esu_expiracao: z.string().nullable(),
    esu_suporte_ativo: z.enum(['S', 'N']),
});

export type EmpresaSuporte = z.infer<typeof EmpresaSuporteSchema>;

export enum EmpresaFintechExtratoTemporario {
    PadraoAntigo = 1,
    PadraoAtual = 2,
    Manutencao = 3,
}

const EmpresaFieldsSchema = z.object({
    emp_bairro: z.string().nullable(),
    emp_cep: z.string().nullable(),
    emp_certificado: z.string().nullable(),
    emp_certificado_csc: z.string().nullable(),
    emp_certificado_csc_id: z.string().nullable(),
    emp_certificado_dataenvio: z.string().nullable(),
    emp_certificado_senha: z.string().nullable(),
    emp_certificado_validade: z.string().nullable(),
    emp_chave_cadastro: z.string().nullable(),
    emp_cidade_idpk: z.number().nullable(),
    emp_cnae: z.string().nullable(),
    emp_cnpj_cpf: z.string().nullable(),
    emp_complemento: z.string().nullable(),
    emp_crt_idpk: z.number().nullable(),
    emp_data_bloqueio: z.string().nullable(),
    emp_data_insercao: z.string().nullable(),
    emp_data_nascimento_abertura: z.string().nullable(),
    emp_doc_estrangeiro: z.string().nullable(),
    emp_email: z.string().nullable(),
    emp_empresa_representante_idpk: z.number().nullable(),
    emp_financeiro_carencia_dias: z.number().nullable(),
    emp_financeiro_categoria_venda_idpk: z.number().nullable(),
    emp_financeiro_juros_mes: z.number(),
    emp_financeiro_multa: z.number(),
    emp_fintech_extrato_temporario: z.enum(EmpresaFintechExtratoTemporario),
    emp_fiscal_ambiente: z.enum(AmbienteFiscal).nullable(),
    emp_fiscal_tipo_emissao: z.enum(EmpresaTipoEmissao),
    emp_idpk: z.number(),
    emp_ie_rg: z.string().nullable(),
    emp_ie_rg_indicador: z.enum(IndicadorIe).nullable(),
    emp_im: z.string().nullable(),
    emp_imposto_funrural_aliquota: z.number().nullable(),
    emp_imposto_funrural_descontar: z.enum(EmpresaImpostoFunruralDescontar).nullable(),
    emp_imposto_funrural_pf_aliquota: z.number().nullable(),
    emp_imposto_funrural_pj_aliquota: z.number().nullable(),
    emp_imposto_icmscredito: z.number().nullable(),
    emp_imposto_permitecredito: z.enum(EmpresaPermiteCredito).nullable(),
    emp_logotipo_dataenvio: z.string().nullable(),
    emp_logotipo_foto: z.string().nullable(),
    emp_logradouro: z.string().nullable(),
    emp_logradouro_numero: z.string().nullable(),
    emp_microempresa: z.enum(EmpresaMicro).nullable(),
    emp_nome_fantasia: z.string().nullable(),
    emp_nome_razaosocial: z.string().nullable(),
    emp_notificacao: z.union([z.literal(0), z.literal(1)]),
    emp_ramoatividade_idpk: z.number().nullable(),
    emp_regra_fiscal_idpk: z.number().nullable(),
    emp_representante_idpk: z.number().nullable(),
    emp_responsavel: z.string().nullable(),
    emp_rg_emissao: z.string().nullable(),
    emp_rg_estado: z.string().nullable(),
    emp_rg_orgao: z.string().nullable(),
    emp_sistema_idpk: z.number(),
    emp_sistema_tipo_uso: z.enum(EmpresaSistemaTipoUso),
    emp_situacao: z.enum(EmpresaSituacao),
    emp_telefone_principal: z.string().nullable(),
    emp_telefone_secundario: z.string().nullable(),
    emp_tipo_pessoa: z.enum(TipoPessoa),
    emp_ultima_alteracao: z.string().nullable(),
    webhook: z.unknown().nullable(),
});

export const EmpresaSchema = EmpresaFieldsSchema.extend({
    cidade: EmpresaCidadeSchema.nullable(),
    // TODO
    contador: z.record(z.string(), z.unknown()).nullable(),
    crt: EmpresaCrtSchema.nullable(),
    empresa_slug: EmpresaSlugSchema.nullable(),
    // TODO
    fintech: z.record(z.string(), z.unknown()).nullable(),
    // TODO
    ramo_atividade: z.record(z.string(), z.unknown()).nullable(),
    representante: RepresentanteSchema.nullable(),
    suporte: EmpresaSuporteSchema.nullable(),
    usuario: EmpresaUsuarioSchema.array().nullish(),
});

export type Empresa = z.infer<typeof EmpresaSchema>;

export type EmpresaBody = DeepUndefinedOrNull<Empresa>;

export type ApiEmpresaContador = {
    emc_empresa_idpk: number;
    emc_relacao_envio_automatico: SimNao;
    emc_relacao_envio_dia: number;
    emc_relacao_envio_xml: SimNao;
    emc_relacao_envio_relacao_fiscal: SimNao;
    emc_relacao_envio_relacao_financeira: SimNao;
    emc_autorizar_autxml: SimNao;
} & Partial<{
    emc_documento: string;
    emc_razaosocial: string;
    emc_logradouro: string;
    emc_logradouro_numero: string;
    emc_logradouro_complemento: string;
    emc_bairro: string;
    emc_cep: string;
    emc_cidade_idpk: number;
    emc_cidade: string;
    emc_uf: string;
    emc_ibge: string;
    emc_fone: string;
    emc_email: string;
    emc_contabilista_nome: string;
    emc_contabilista_documento: string;
    emc_contabilista_crc: string;
}>;

export const EmpresaRamoAtividadeSchema = z.object({
    era_idpk: z.number(),
    era_ramointerno: z.number(),
    era_tipo: z.string(),
});

export type EmpresaRamoAtividade = z.infer<typeof EmpresaRamoAtividadeSchema>;
